import validUrl from 'airship/modules/url-is-valid'
import {
  ValidationError,
  ValidationHandler,
  ErrorType,
} from 'airship/components/Validator/types'
import {parseUrlUsingSquareBrackets, extractHostname} from './utils'
import {ResponsePath} from './reducer'
import {ExternalDataFeedObject} from 'airship/clients/xdf/types'

const MAX_PLACEHOLDERS = 5
export const MAX_PLACEHOLDERS_ERROR_MESSAGE = `only ${MAX_PLACEHOLDERS} placeholders are allowed`

export function isValidUrlWithSquareHandlebars(): ValidationHandler<string> {
  // `placeholders` are handlebars variables with `[[]]` instead of `{{}}`
  // so this is valid...
  // http://foo.com/[[bar]]
  return function validator(data = '') {
    const parsed = parseUrlUsingSquareBrackets(data)
    let url = ''
    parsed.forEach((token) => {
      if (token.type === 'text') {
        url += token.content
      } else {
        url += 'x'
      }
    })

    const countOfPlaceholders = parsed.filter((t) => t.type === 'substitution')
      .length

    if (!validUrl(url)) {
      throw new ValidationError('is an invalid URL', ErrorType.InvalidURL)
    }

    if (url.includes('[[]]')) {
      throw new ValidationError(
        'empty placeholder',
        ErrorType.XdfEmptyPlaceholder
      )
    }

    if (countOfPlaceholders > MAX_PLACEHOLDERS) {
      throw new ValidationError(
        MAX_PLACEHOLDERS_ERROR_MESSAGE,
        ErrorType.XdfTooManyPlaceholders
      )
    }
  }
}

export function domainMatchesProvidedURL(
  url: string
): ValidationHandler<string> {
  return function validator(data = '') {
    if (extractHostname(url) !== data) {
      throw new ValidationError(
        'Enter only the domain name of your request URL',
        ErrorType.DomainDoesNotMatchUrl
      )
    }
  }
}

export function uniqueResponseMappingName(
  responsePaths: readonly ResponsePath[],
  id: string
): ValidationHandler<string> {
  return function validator(data = '') {
    // we're not checking for emptiness here.
    if (!data) return

    if (
      responsePaths.filter((value) => {
        return value.id !== id && value.name === data
      }).length
    ) {
      throw new ValidationError('already exists', ErrorType.DuplicateExists)
    }
  }
}

export function uniqueFeedIDWithinApp(
  feedList: readonly ExternalDataFeedObject[],
  idCurrentlyEditing?: string
): ValidationHandler<string> {
  return function validator(data = '') {
    // we're not checking for emptiness here.
    if (!data) return

    const feedsWithMatchingFeedID = feedList.filter((feed) => {
      if (idCurrentlyEditing && data === idCurrentlyEditing) {
        // don't worry - the feed is just matching itself.
        return false
      }
      if (feed.name === data) {
        return true
      }
    })

    if (feedsWithMatchingFeedID.length) {
      throw new ValidationError(
        'a feed already exists with this feed ID',
        ErrorType.FeedWithThisIDAlreadyExists
      )
    }
  }
}
